import type { Id } from "@convex/_generated/dataModel";
import { formatCentsAsCurrency } from "@plasma/utils/money-formatters";
import { formatDate } from "@plasma/utils/time";
import { useQuery } from "convex/react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  ExternalLink,
  Package,
  Truck,
  XCircle,
} from "lucide-react";

import { hardwareOrdersListOrders } from "~/api-refs";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Spinner } from "~/components/ui/spinner";
import { logger, PortalEvents } from "~/lib/logger";
import { enumIncludes } from "~/lib/parse-enum-value";

interface OrderHistoryProps {
  locationId?: Id<"locations">;
  limit?: number;
  onCancelOrder?: (orderId: Id<"hardwareOrders">) => void;
}

function formatOrderDate(timestamp: number): string {
  return formatDate(timestamp, "MMM D, YYYY");
}

const ORDER_STATUSES = [
  "pending",
  "ready_to_ship",
  "shipped",
  "delivered",
  "canceled",
  "undeliverable",
] as const;
type OrderStatus = (typeof ORDER_STATUSES)[number];
const isOrderStatus = enumIncludes(ORDER_STATUSES);

const statusConfig: Record<
  OrderStatus,
  {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline";
    icon: React.ElementType;
  }
> = {
  pending: { label: "Processing", variant: "secondary", icon: Clock },
  ready_to_ship: { label: "Ready to Ship", variant: "default", icon: Package },
  shipped: { label: "Shipped", variant: "default", icon: Truck },
  delivered: { label: "Delivered", variant: "default", icon: CheckCircle2 },
  canceled: { label: "Canceled", variant: "destructive", icon: XCircle },
  undeliverable: {
    label: "Undeliverable",
    variant: "destructive",
    icon: AlertTriangle,
  },
};

export function OrderHistory({
  locationId,
  limit,
  onCancelOrder,
}: OrderHistoryProps) {
  const orders = useQuery(hardwareOrdersListOrders, {
    locationId,
    limit,
  });

  if (orders === undefined) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="size-5" />
            Recent Orders
          </CardTitle>
          <CardDescription>Track your card reader orders</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Spinner className="size-6" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (orders.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="size-5" />
            Recent Orders
          </CardTitle>
          <CardDescription>Track your card reader orders</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Package className="text-muted-foreground/30 mb-2 size-10" />
            <p className="text-muted-foreground text-sm">No orders yet</p>
            <p className="text-muted-foreground mt-1 text-xs">
              Order readers using the "Order Card Reader" action above.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="size-5" />
          Recent Orders
        </CardTitle>
        <CardDescription>Track your card reader orders</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="divide-y">
          {orders.map((order) => {
            const status = isOrderStatus(order.status)
              ? statusConfig[order.status]
              : {
                  label: order.status,
                  variant: "outline" as const,
                  icon: Package,
                };
            const StatusIcon = status.icon;
            const canCancel = ![
              "ready_to_ship",
              "shipped",
              "delivered",
              "canceled",
            ].includes(order.status);

            return (
              <div className="py-4 first:pt-0 last:pb-0" key={order._id}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    {/* Order header */}
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className="gap-1" variant={status.variant}>
                        <StatusIcon className="size-3" />
                        {status.label}
                      </Badge>
                      <span className="text-muted-foreground text-xs">
                        {formatOrderDate(order.createdAt)}
                      </span>
                    </div>

                    {/* Items */}
                    <div className="mt-2">
                      {order.items.map((item) => (
                        <p
                          className="text-sm"
                          key={`${item.productName}-${item.quantity}`}
                        >
                          {item.quantity}x {item.productName}
                        </p>
                      ))}
                    </div>

                    {!locationId && order.locationName && (
                      <p className="text-muted-foreground mt-1 text-xs">
                        For: {order.locationName}
                      </p>
                    )}

                    {/* Tracking */}
                    {order.status === "shipped" && order.trackingUrl && (
                      <a
                        className="text-primary mt-2 inline-flex items-center gap-1 text-xs hover:underline"
                        href={order.trackingUrl}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Track Package
                        <ExternalLink className="size-3" />
                      </a>
                    )}

                    {/* Delivery info */}
                    {order.status === "delivered" && order.deliveredAt && (
                      <p className="text-success mt-1 text-xs">
                        Delivered on {formatOrderDate(order.deliveredAt)}
                      </p>
                    )}

                    {/* Undeliverable warning */}
                    {order.status === "undeliverable" && (
                      <p className="text-destructive mt-1 text-xs">
                        Delivery failed. Please contact support.
                      </p>
                    )}
                  </div>

                  <div className="shrink-0 text-right">
                    <p className="font-medium">
                      {formatCentsAsCurrency(order.totalAmount)}
                    </p>
                    {canCancel && onCancelOrder && (
                      <Button
                        className="text-destructive hover:text-destructive mt-1"
                        onClick={() => {
                          logger.info(PortalEvents.ORDER_CANCEL_CLICKED, {
                            order_id: order._id,
                            order_status: order.status,
                            location_id: locationId,
                          });
                          onCancelOrder(order._id);
                        }}
                        size="sm"
                        type="button"
                        variant="ghost"
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
